import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, shareReplay } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { RestfulEndpoint } from '../restful-endpoint.service';
import {
  SyncResult,
  IntegrationStatistics,
  ConnectionTestResponse,
  SyncOptions,
} from '../../models/integration.model';
import { BaseResponse } from '../../models/shared.model';
import { CustomFieldsResponse } from '../../models/custom-fields.model';
import { environment } from '../../../environments/environment';
import { UserService } from '../../core/user/user.service';

/**
 * Integration Service for Whitehelmet-NWC Unifier Integration
 * Handles all integration-related API calls and data management
 * Following existing service patterns in the codebase
 */
@Injectable({
  providedIn: 'root',
})
export class IntegrationService {
  private readonly API_BASE = 'integration';
  private readonly API_V2_BASE = 'v2/integration';

  // Cache for custom fields observables to prevent duplicate API calls
  // Key format: "clientId|userId|projectId|observationType|observationId"
  // Each entry is a shared observable that multiple subscribers can use
  private customFieldsCache = new Map<string, Observable<CustomFieldsResponse>>();

  // Provider configuration
  private readonly PROVIDER_CONFIG = {
    projects: {
      syncEndpoint: 'integration/projects/sync/queue',
      testDataEndpoint: 'integration/projects/sync/test-with-data',
      testConnectionEndpoint: 'integration/projects/sync/test-connection',
    },
    p6: {
      syncEndpoint: (projectId: string) => `integration/projects/${projectId}/p6-metrics/sync`,
      testConnectionEndpoint: 'integration/projects/sync/test-connection', // Reuse projects endpoint for now
    },
    observations: {
      syncEndpoint: (projectId: string) =>
        `integration/projects/${projectId}/observations/inbound-sync`,
      testConnectionEndpoint: 'integration/projects/sync/test-connection', // Reuse projects endpoint for now
    },
  };

  constructor(
    private endPoint: RestfulEndpoint,
    private userService: UserService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Generic Sync Operations
  // -----------------------------------------------------------------------------------------------------

  /**
   * Generic sync method for all providers
   *
   * @param provider - The provider type ('projects', 'p6', 'observations')
   * @param options - Sync options including testMode and projectId
   * @returns Observable<SyncResult>
   */
  sync(provider: 'projects' | 'p6' | 'observations', options: SyncOptions): Observable<SyncResult> {
    const { testMode, projectId, customData } = options;

    // Determine environment based on current configuration
    const currentEnvironment = this.getCurrentEnvironment();

    switch (provider) {
      case 'projects':
        return this.syncProjectsInternal(testMode, currentEnvironment, customData);
      case 'p6':
        if (!projectId) {
          return throwError(() => new Error('Project ID is required for P6 sync'));
        }
        return this.syncP6Internal(projectId, testMode, currentEnvironment);
      case 'observations':
        if (!projectId) {
          return throwError(() => new Error('Project ID is required for observations sync'));
        }
        return this.syncObservationsInternal(projectId, testMode, currentEnvironment);
      default:
        return throwError(() => new Error(`Unsupported provider: ${provider}`));
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Provider-Specific Sync Operations (Backward Compatible)
  // -----------------------------------------------------------------------------------------------------

  /**
   * Sync projects from Unifier
   * POST /api/v1/integration/projects/sync
   *
   * @returns Observable<SyncResult>
   */
  syncProjects(testMode: boolean): Observable<SyncResult> {
    return this.sync('projects', { testMode });
  }

  /**
   * Sync P6 metrics for a project
   * POST /api/v1/integration/projects/{projectId}/p6-metrics/sync
   *
   * @param projectId - The project ID
   * @param testMode - Whether to run in test mode
   * @returns Observable<SyncResult>
   */
  syncP6(projectId: string, testMode: boolean): Observable<SyncResult> {
    return this.sync('p6', { projectId, testMode: false });
  }

  /**
   * Sync observations for a project
   * POST /api/v1/integration/projects/{projectId}/observations/inbound-sync
   *
   * @param projectId - The project ID
   * @param testMode - Whether to run in test mode
   * @returns Observable<SyncResult>
   */
  syncObservations(projectId: string, testMode: boolean): Observable<SyncResult> {
    return this.sync('observations', { projectId, testMode: false });
  }

  /**
   * Sync projects with custom JSON data
   * POST /api/v1/integration/projects/sync-with-data
   *
   * @param jsonData - Custom JSON data in NWC format
   * @param testMode - Whether to run in test mode
   * @returns Observable<SyncResult>
   */
  syncProjectsWithData(jsonData: any, testMode: boolean): Observable<SyncResult> {
    return this.sync('projects', { testMode, customData: jsonData });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Internal Sync Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Internal method for projects sync
   */
  private syncProjectsInternal(
    testMode: boolean,
    environment: string,
    customData?: any,
  ): Observable<SyncResult> {
    if (customData) {
      return this.endPoint
        .post(this.PROVIDER_CONFIG.projects.testDataEndpoint, customData)
        .pipe(catchError(this.handleError.bind(this)));
    }

    return this.endPoint
      .post(this.PROVIDER_CONFIG.projects.syncEndpoint, {
        test_mode: testMode,
        environment: environment,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Internal method for P6 sync
   */
  private syncP6Internal(
    projectId: string,
    testMode: boolean,
    environment: string,
  ): Observable<SyncResult> {
    return this.endPoint
      .post(this.PROVIDER_CONFIG.p6.syncEndpoint(projectId), {
        testMode: testMode,
        environment: environment,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Internal method for observations sync
   */
  private syncObservationsInternal(
    projectId: string,
    testMode: boolean,
    environment: string,
  ): Observable<SyncResult> {
    return this.endPoint
      .post(this.PROVIDER_CONFIG.observations.syncEndpoint(projectId), {
        testMode: testMode,
        environment: environment,
      })
      .pipe(catchError(this.handleError.bind(this)));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Integration Status Operations
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get integration status for a specific project
   * NOTE: This endpoint is not yet implemented on the backend
   *
   * @param projectId - The project ID
   * @returns Observable<IntegrationStatus>
   */
  // getIntegrationStatus(projectId: string): Observable<IntegrationStatus> {
  // 	return this.endPoint
  // 		.post(`projects/${projectId}/integration-status`, {})
  // 		.pipe(catchError(this.handleError.bind(this)));
  // }

  /**
   * Get integration status for multiple projects
   * NOTE: This endpoint is not yet implemented on the backend
   *
   * @param projectIds - Array of project IDs
   * @returns Observable<{[projectId: string]: IntegrationStatus}>
   */
  // getMultipleIntegrationStatus(
  // 	projectIds: string[],
  // ): Observable<{ [projectId: string]: IntegrationStatus }> {
  // 	return this.endPoint
  // 		.post(`projects/integration-status/bulk`, { project_ids: projectIds })
  // 		.pipe(catchError(this.handleError.bind(this)));
  // }

  // -----------------------------------------------------------------------------------------------------
  // @ Integration Statistics and Dashboard
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get integration statistics
   * GET /api/v1/integration/projects/sync/statistics
   *
   * @returns Observable<IntegrationStatistics>
   */
  getIntegrationStatistics(): Observable<IntegrationStatistics> {
    return this.endPoint
      .get(`v1/integration/projects/sync/statistics`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Get integration dashboard data
   * NOTE: This endpoint is not yet implemented on the backend
   *
   * @returns Observable<IntegrationDashboardData>
   */
  // getIntegrationDashboard(): Observable<IntegrationDashboardData> {
  // 	return this.endPoint
  // 		.post(`${this.API_BASE}/dashboard`, {})
  // 		.pipe(catchError(this.handleError.bind(this)));
  // }

  // -----------------------------------------------------------------------------------------------------
  // @ Sync History Operations
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get sync history with pagination
   * NOTE: This endpoint is not yet implemented on the backend
   *
   * @param page - Page number
   * @param perPage - Items per page
   * @returns Observable<SyncHistoryResponse>
   */
  // getSyncHistory(
  // 	page: number = 1,
  // 	perPage: number = 20,
  // ): Observable<SyncHistoryResponse> {
  // 	return this.endPoint
  // 		.post(`${this.API_BASE}/sync-history`, {
  // 			page,
  // 			per_page: perPage,
  // 		})
  // 		.pipe(catchError(this.handleError.bind(this)));
  // }

  /**
   * Get sync history for a specific project
   * NOTE: This endpoint is not yet implemented on the backend
   *
   * @param projectId - The project ID
   * @param page - Page number
   * @param perPage - Items per page
   * @returns Observable<SyncHistoryResponse>
   */
  // getProjectSyncHistory(
  // 	projectId: string,
  // 	page: number = 1,
  // 	perPage: number = 20,
  // ): Observable<SyncHistoryResponse> {
  // 	return this.endPoint
  // 		.post(`${this.API_BASE}/projects/${projectId}/sync-history`, {
  // 			page,
  // 			per_page: perPage,
  // 		})
  // 		.pipe(catchError(this.handleError.bind(this)));
  // }

  // -----------------------------------------------------------------------------------------------------
  // @ Configuration Operations
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get integration configuration
   * NOTE: This endpoint is not yet implemented on the backend
   *
   * @returns Observable<IntegrationConfig>
   */
  // getIntegrationConfig(): Observable<IntegrationConfig> {
  // 	return this.endPoint
  // 		.post(`${this.API_BASE}/config`, {})
  // 		.pipe(catchError(this.handleError.bind(this)));
  // }

  /**
   * Update integration configuration
   * NOTE: This endpoint is not yet implemented on the backend
   *
   * @param config - Configuration data
   * @returns Observable<IntegrationConfig>
   */
  // updateIntegrationConfig(
  // 	config: Partial<IntegrationConfig>,
  // ): Observable<IntegrationConfig> {
  // 	return this.endPoint
  // 		.post(`${this.API_BASE}/config/update`, config)
  // 		.pipe(catchError(this.handleError.bind(this)));
  // }

  // -----------------------------------------------------------------------------------------------------
  // @ Connection Testing
  // -----------------------------------------------------------------------------------------------------

  /**
   * Test connection to Unifier API
   * POST /api/v1/integration/projects/sync/test-connection
   *
   * @returns Observable<ConnectionTestResponse>
   */
  testUnifierConnection(): Observable<ConnectionTestResponse> {
    return this.endPoint
      .post(this.PROVIDER_CONFIG.projects.testConnectionEndpoint, {})
      .pipe(catchError(this.handleError.bind(this)));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Custom Fields Operations
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get custom fields for a specific observation type
   * GET /api/integration/projects/{projectId}/custom-fields?observation_type={type}&observation_id={id?}
   *
   * Uses shareReplay() to share in-flight requests and replay the latest value while subscribed.
   * - Multiple subscribers reuse the same HTTP request (prevents duplicate calls)
   * - refCount clears the replay cache when all subscribers unsubscribe
   * - Errors remove the observable from the local cache so the next request can retry
   *
   * @param projectId - The project ID
   * @param observationType - The observation type to get custom fields for
   * @param observationId - Optional existing observation id; when provided the BE
   *                       can return creation-locked fields (e.g. NCR Type) as read-only.
   * @returns Observable<CustomFieldsResponse>
   */
  getCustomFields(
    projectId: string,
    observationType: string,
    observationId?: string | number | null,
  ): Observable<CustomFieldsResponse> {
    // Get current user info for cache key
    const user = this.userService.getLoggedInUser();
    const userId = user?.user_id || 'anonymous';
    const clientId = user?.client_id || 'unknown';

    const normalizedObservationId =
      observationId !== undefined && observationId !== null && observationId !== ''
        ? observationId
        : null;

    if (!environment.production) {
      console.log('🔔 [IntegrationService.getCustomFields] Requesting custom fields:', {
        projectId,
        observationType,
        observationId: normalizedObservationId,
      });
    }

    // Create cache key from projectId, observationType, observationId, userId, and clientId.
    // observation_id changes the schema (read_only_after_creation fields), so it must be
    // part of the cache key to avoid leaking creation-flow schema into edit-flow callers.
    const observationIdKey = normalizedObservationId ?? 'none';
    const cacheKey = `${clientId}|${userId}|${projectId}|${observationType}|${observationIdKey}`;

    // Check if we already have a cached observable for this combination
    let cachedObservable = this.customFieldsCache.get(cacheKey);

    if (!cachedObservable) {
      if (!environment.production) {
        console.log('🌐 [IntegrationService] Creating new API observable for:', {
          projectId,
          observationType,
          observationId: normalizedObservationId,
        });
      }

      const queryParams: Record<string, string | number> = {
        observation_type: observationType,
      };
      if (normalizedObservationId !== null) {
        queryParams['observation_id'] = normalizedObservationId;
      }

      // Create new observable with shareReplay
      // CACHE DURATION: Cached as long as at least 1 component is subscribed
      // - When refCount reaches 0 (all components unsubscribe), cache clears automatically
      // - Next subscription triggers a fresh HTTP request
      // - No time-based expiration
      //
      // shareReplay({ bufferSize: 1, refCount: true }) behavior:
      // 1. Multiple simultaneous subscribers share the same HTTP request (prevents duplicates)
      // 2. Late subscribers (who subscribe after HTTP completes) get the cached result
      // 3. When all subscribers unsubscribe (refCount = 0), shareReplay resets
      // 4. Next subscription after reset triggers a new HTTP request
      cachedObservable = this.endPoint
        .get(`${this.API_BASE}/projects/${projectId}/custom-fields`, queryParams, null, {
          version: '',
        })
        .pipe(
          catchError((error) => {
            console.error('IntegrationService.getCustomFields API error:', error);
            // Remove from cache on error so next request will retry
            this.customFieldsCache.delete(cacheKey);
            return this.handleError(error);
          }),
          shareReplay({
            bufferSize: 1,
            refCount: true,
          }),
        );

      // Store in cache
      this.customFieldsCache.set(cacheKey, cachedObservable);
    } else {
      if (!environment.production) {
        console.log('📦 [IntegrationService] Reusing cached observable for:', {
          projectId,
          observationType,
          observationId: normalizedObservationId,
        });
      }
    }

    return cachedObservable;
  }

  /**
   * Clear the custom fields cache for a specific project and observation type
   * Useful when you know the data has changed and want to force a refresh
   */
  clearCustomFieldsCache(projectId?: string, observationType?: string): void {
    if (projectId && observationType) {
      const user = this.userService.getLoggedInUser();
      const userId = user?.user_id || 'anonymous';
      const clientId = user?.client_id || 'unknown';
      const legacyCacheKey = `${clientId}|${userId}|${projectId}|${observationType}`;
      const cacheKeyPrefix = `${legacyCacheKey}|`;
      let clearedEntries = 0;

      for (const cacheKey of this.customFieldsCache.keys()) {
        if (cacheKey === legacyCacheKey || cacheKey.startsWith(cacheKeyPrefix)) {
          this.customFieldsCache.delete(cacheKey);
          clearedEntries++;
        }
      }

      if (!environment.production) {
        console.log('🧹 [IntegrationService] Cleared custom fields cache entries:', {
          projectId,
          observationType,
          clearedEntries,
        });
      }
    } else {
      // Clear all cache entries
      this.customFieldsCache.clear();
      if (!environment.production) {
        console.log('🧹 [IntegrationService] Cleared entire custom fields cache');
      }
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Utility Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get current environment based on configuration
   */
  private getCurrentEnvironment(): 'uat' | 'production' {
    if (environment.production) {
      return 'production';
    } else if (environment.env === 'staging' || environment.env === 'pre-prod') {
      return 'uat';
    } else {
      // For local development, default to uat
      return 'uat';
    }
  }

  /**
   * Handle HTTP errors
   *
   * @param error - The HTTP error response
   * @returns Observable<never>
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('Integration Service Error:', error);

    // Let the error notification service handle user-facing errors
    // This service focuses on API communication
    return throwError(error);
  }
}
